/**
 * /super-admin/sms — SMS Reminders & Outbox
 * ───────────────────────────────────────────
 * SA configures auto-SMS rules + reviews everything that's gone out:
 *   - Rules: pair an SMS template with a trigger
 *       • on_booking_created   → immediate when a booking is created
 *       • daily_at HH:MM        → reminds tomorrow's bookings every day
 *       • n_hours_before        → X-hour-before-booking reminder
 *   - Outbox: scrollable feed of every send attempt (sent / dry_run / failed).
 *
 * SMS templates are created in /super-admin/templates with template_type=sms
 * (re-used here via the templates dropdown).
 */
import { useEffect, useMemo, useState } from 'react';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Phone, Plus, Trash2, CheckCircle2, AlertCircle, Clock, Send, Loader2, Inbox,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TRIGGER_LABELS = {
  on_booking_created: 'On booking created',
  daily_at: 'Daily at fixed time',
  n_hours_before: 'N hours before booking',
};

const STATUS_BADGES = {
  sent: { bg: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  dry_run: { bg: 'bg-blue-100 text-blue-700', icon: Inbox },
  failed: { bg: 'bg-rose-100 text-rose-700', icon: AlertCircle },
  queued: { bg: 'bg-slate-100 text-slate-700', icon: Clock },
};

export default function SuperAdminSms() {
  const [rules, setRules] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [counts, setCounts] = useState({});
  const [keyConfigured, setKeyConfigured] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(initialForm());
  const [saving, setSaving] = useState(false);

  function initialForm() {
    return { name: '', template_id: '', trigger: 'daily_at', send_at_local: '18:30', n_hours_before: 24, enabled: true };
  }

  const fetchAll = async () => {
    try {
      const [rRules, rOut, rTpl] = await Promise.all([
        fetch(`${BACKEND_URL}/api/sms/rules`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/sms/outbox?limit=200`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/templates?template_type=sms`, { credentials: 'include' }),
      ]);
      if (rRules.ok) {
        const d = await rRules.json();
        setRules(d.rules || []);
      }
      if (rOut.ok) {
        const d = await rOut.json();
        setOutbox(d.rows || []);
        setCounts(d.counts || {});
        setKeyConfigured(!!d.key_configured);
      }
      if (rTpl.ok) {
        const d = await rTpl.json();
        setTemplates(Array.isArray(d) ? d : (d.templates || []));
      }
    } catch (_e) { /* noop */ }
  };

  useEffect(() => { fetchAll(); }, []);

  const saveRule = async () => {
    if (!form.name.trim() || !form.template_id) {
      toast.error('Name + template are required');
      return;
    }
    if (form.trigger === 'daily_at' && !/^\d{2}:\d{2}$/.test(form.send_at_local || '')) {
      toast.error('Daily trigger needs HH:MM time');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/sms/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (r.ok) {
        toast.success('Rule created');
        setShowCreate(false); setForm(initialForm());
        fetchAll();
      } else {
        const e = await r.json();
        toast.error(e.detail || 'Failed to create rule');
      }
    } catch (_e) { toast.error('Network error'); }
    finally { setSaving(false); }
  };

  const toggleEnabled = async (rule) => {
    const r = await fetch(`${BACKEND_URL}/api/sms/rules/${rule.rule_id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ enabled: !rule.enabled }),
    });
    if (r.ok) { fetchAll(); }
    else { toast.error('Failed to toggle rule'); }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    const r = await fetch(`${BACKEND_URL}/api/sms/rules/${rule.rule_id}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (r.ok) { toast.success('Rule deleted'); fetchAll(); }
    else { toast.error('Failed to delete'); }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="sa-sms-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Phone className="h-6 w-6 text-emerald-600" />
              SMS Reminders & Outbox
            </h1>
            <p className="text-sm text-slate-500">Schedule automated SMS to customers and audit every send.</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sa-sms-create">
            <Plus className="h-4 w-4 mr-1" /> New Rule
          </Button>
        </div>

        {!keyConfigured && (
          <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="sa-sms-dryrun-banner">
            <span className="font-semibold">Dry-run mode</span> — <code className="text-xs">CELLCAST_APPKEY</code> is not configured. All scheduled and manual sends are recorded to the outbox so you can verify the content, but no real SMS is sent until the key is added to <code className="text-xs">backend/.env</code>.
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Stat label="Sent" value={counts.sent || 0} colour="emerald" />
          <Stat label="Dry-run" value={counts.dry_run || 0} colour="blue" />
          <Stat label="Failed" value={counts.failed || 0} colour="rose" />
        </div>

        <Card className="p-4 mb-6">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" /> Active Rules
          </h2>
          {rules.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">
              No rules yet. Create one to start auto-SMS reminders.
            </p>
          ) : (
            <div className="space-y-2">
              {rules.map(r => {
                const tpl = templates.find(t => t.template_id === r.template_id);
                return (
                  <div key={r.rule_id} className="flex items-center justify-between gap-3 p-3 rounded border bg-white" data-testid={`sa-sms-rule-${r.rule_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900">{r.name}</p>
                        {!r.enabled && <Badge variant="outline" className="text-slate-500">paused</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Trigger: <span className="font-medium text-slate-700">{TRIGGER_LABELS[r.trigger] || r.trigger}</span>
                        {r.trigger === 'daily_at' && r.send_at_local && (
                          <> · at <span className="font-medium text-slate-700">{r.send_at_local}</span> Melbourne</>
                        )}
                        {r.trigger === 'n_hours_before' && r.n_hours_before != null && (
                          <> · <span className="font-medium text-slate-700">{r.n_hours_before}h</span> before booking</>
                        )}
                        {tpl && <> · Template: <span className="font-medium text-slate-700">{tpl.name}</span></>}
                      </p>
                    </div>
                    <Switch checked={!!r.enabled} onCheckedChange={() => toggleEnabled(r)} data-testid={`sa-sms-rule-toggle-${r.rule_id}`} />
                    <Button variant="ghost" size="sm" onClick={() => deleteRule(r)} className="text-rose-500 hover:text-rose-700" data-testid={`sa-sms-rule-delete-${r.rule_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-4">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Inbox className="h-4 w-4" /> Outbox <span className="text-xs font-normal text-slate-500">— most recent 200</span>
          </h2>
          {outbox.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No SMS sent yet.</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {outbox.map(o => {
                const cfg = STATUS_BADGES[o.status] || STATUS_BADGES.queued;
                const Icon = cfg.icon;
                return (
                  <div key={o.outbox_id} className="p-3 rounded border bg-white text-sm" data-testid={`sa-sms-outbox-${o.outbox_id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={cfg.bg + ' hover:' + cfg.bg}>
                        <Icon className="h-3 w-3 mr-1" />
                        {o.status}
                      </Badge>
                      <span className="text-xs text-slate-500">{o.to_mobile || '—'}</span>
                      <span className="text-xs text-slate-400 ml-auto">{o.created_at?.slice(0, 19).replace('T', ' ') || '—'}</span>
                    </div>
                    <p className="text-slate-800 whitespace-pre-wrap text-[13px]">{o.body}</p>
                    {o.error && <p className="text-xs text-rose-700 mt-1">{o.error}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="sa-sms-create-dialog">
          <DialogHeader>
            <DialogTitle>New SMS Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Tomorrow's reminder · 6:30 PM"
                className="mt-1"
                data-testid="sa-sms-form-name"
              />
            </div>
            <div>
              <Label className="text-xs">SMS Template</Label>
              <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
                <SelectTrigger className="mt-1" data-testid="sa-sms-form-template">
                  <SelectValue placeholder={templates.length === 0 ? 'Create SMS templates first in /super-admin/templates' : 'Pick an SMS template…'} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Trigger</Label>
              <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                <SelectTrigger className="mt-1" data-testid="sa-sms-form-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="on_booking_created">On booking created (immediate)</SelectItem>
                  <SelectItem value="daily_at">Daily at fixed time</SelectItem>
                  <SelectItem value="n_hours_before">N hours before booking</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.trigger === 'daily_at' && (
              <div>
                <Label className="text-xs">Send at (Melbourne wallclock, HH:MM)</Label>
                <Input
                  type="time"
                  value={form.send_at_local}
                  onChange={(e) => setForm({ ...form, send_at_local: e.target.value })}
                  className="mt-1"
                  data-testid="sa-sms-form-time"
                />
              </div>
            )}
            {form.trigger === 'n_hours_before' && (
              <div>
                <Label className="text-xs">Hours before booking</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.n_hours_before}
                  onChange={(e) => setForm({ ...form, n_hours_before: parseInt(e.target.value || '0', 10) })}
                  className="mt-1"
                  data-testid="sa-sms-form-hours"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={saveRule} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sa-sms-form-save">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Save Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Stat({ label, value, colour }) {
  const colours = {
    emerald: 'border-l-emerald-500 text-emerald-700',
    blue: 'border-l-blue-500 text-blue-700',
    rose: 'border-l-rose-500 text-rose-700',
  };
  return (
    <Card className={`p-4 border-l-4 ${colours[colour] || colours.emerald}`}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
    </Card>
  );
}
